/**
 * §16 V1 Mesh — shared topology canvas used by both:
 *   - AnalysisDetailPage Topology tab (scope=analysis)
 *   - TopologyPage (scope=tenant)
 *
 * Wraps react-flow + elkjs layout + the custom node/edge renderers, with
 * overlay toggles (risk / framework / compliance) and a callback for
 * clicked nodes (parent owns the side drawer).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  type Edge,
  type Node,
  type NodeMouseHandler,
  type EdgeMouseHandler,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { layoutMesh } from './layout'
import { MeshNodeView, RepoHeaderView } from './nodeRenderers'
import { MeshArrowDefs, MeshEdgeView } from './edgeRenderers'
import LensLegend from './LensLegend'
import { legendEntryFor } from './nodeRenderers'
import OrientationHint from './OrientationHint'
import type { MeshNode, MeshResponse, OverlayMode } from './types'

const nodeTypes = {
  mesh: MeshNodeView,
  repoHeader: RepoHeaderView,
}

const edgeTypes = {
  mesh: MeshEdgeView,
}

interface MeshViewProps {
  mesh: MeshResponse
  loading?: boolean
  error?: string | null
  onNodeClick?: (node: MeshNode) => void
  onEdgeClick?: (edgeId: string) => void
  height?: number | string
  /** Frontend-only filter for tenant scope (parent passes a controlled repoId) */
  /** Which repositories to show. Empty means all of them. */
  repoFilter?: string[]
  onRepoFilterChange?: (repoIds: string[]) => void
  /** M5b — when overlay becomes 'compliance', parent re-fetches mesh with lens
   *  params. Parent owns the framework state because it also drives the
   *  drill-down side panel.
   */
  overlay?: OverlayMode
  onOverlayChange?: (overlay: OverlayMode) => void
  complianceFramework?: string | null
  availableFrameworks?: Array<{ key: string; display_name?: string }>
  onComplianceFrameworkChange?: (framework: string | null) => void
  /** AR3 — when set, the canvas auto-zooms into the matching node on
   *  mount. Used by /topology?focus=<agent_id_hint>. Parent reads the
   *  URL param and passes it through. */
  initialFocusNodeId?: string | null
  /** Escalation-path UI fix (2026-07-29) — ordered agent_id_hints of an
   *  active IACD-PERM-001 escalation path, set from the Findings tab's
   *  "View escalation path" CTA. Orthogonal to `overlay` — a specific,
   *  transient answer to one finding, not a persistent lens the user
   *  toggles (see the 5 overlay modes above), so it stays visible
   *  alongside whichever lens is already active, same as the existing
   *  initialFocusNodeId zoom mechanism this generalizes. Do not repurpose
   *  initialFocusNodeId itself — it has its own existing caller
   *  (TopologyPage.tsx's `?focus=` deep link) that must keep working
   *  unchanged for the single-node case. */
  focusPath?: string[] | null
}

/** Stable identity for "no repo filter applied".
 *
 * Selecting several repositories is a Topology-page feature: that view is
 * tenant-wide, and the picker is gated on `scope === 'tenant'`. An analysis is
 * already scoped to the repository it ran on, so it never passes this prop and
 * always takes the default.
 *
 * Which is why the default cannot be a literal. Written inline as `[]` it is a
 * new array on every render, and `repoFilter` is a dependency of the
 * `visibleMesh` memo that feeds the layout effect — so the memo recomputed,
 * the effect re-ran, `setLaying` re-rendered, and the next render built
 * another `[]`. The analysis Topology tab flickered and never settled, while
 * the Topology page was fine because it passes an array held in state.
 *
 * It replaced `repoFilter = null` in 4960557; a primitive default compared
 * equal every render and hid the whole problem.
 */
const NO_REPO_FILTER: string[] = []

export default function MeshView({
  mesh,
  loading = false,
  error = null,
  onNodeClick,
  onEdgeClick,
  height = 600,
  repoFilter = NO_REPO_FILTER,
  onRepoFilterChange,
  overlay: overlayProp,
  onOverlayChange,
  complianceFramework = null,
  availableFrameworks = [],
  onComplianceFrameworkChange,
  initialFocusNodeId = null,
  focusPath = null,
}: MeshViewProps) {
  const [internalOverlay, setInternalOverlay] = useState<OverlayMode>('risk')
  const overlay = overlayProp ?? internalOverlay
  const setOverlay = (next: OverlayMode) => {
    if (onOverlayChange) onOverlayChange(next)
    else setInternalOverlay(next)
  }
  // M6 — Zoom-into-neighborhood: when set to a node id, filter the
  // visible mesh to {that node} ∪ {nodes connected by any edge to it}.
  // Frontend-only; the backend always returns the full mesh.
  const [zoomedNodeId, setZoomedNodeId] = useState<string | null>(initialFocusNodeId)
  // AR3 — re-honor the URL focus param when it changes (e.g., user
  // navigates between two registry rows without remounting).
  useEffect(() => {
    if (initialFocusNodeId) setZoomedNodeId(initialFocusNodeId)
  }, [initialFocusNodeId])
  const zoomedNode = zoomedNodeId
    ? mesh.nodes.find((n) => n.id === zoomedNodeId) || null
    : null

  // Escalation-path UI fix (2026-07-29) — generalizes the zoom-into-
  // neighborhood mechanism above to an ORDERED multi-node path, instead
  // of one node + its 1-hop neighbors. Kept as separate state (not
  // reusing zoomedNodeId) so the two remain independently controllable —
  // shift-clicking a node while a path is active clears the path rather
  // than fighting it for which neighborhood wins.
  const [activeFocusPath, setActiveFocusPath] = useState<string[] | null>(
    focusPath && focusPath.length > 0 ? focusPath : null,
  )
  useEffect(() => {
    if (focusPath && focusPath.length > 0) setActiveFocusPath(focusPath)
  }, [focusPath])
  // M7b — Edge-confidence filter: 'all' | 'observed' | 'inferred'
  const [edgeFilter, setEdgeFilter] = useState<'all' | 'observed' | 'inferred'>('all')
  // Which node type the legend is picking out, as `type` or
  // `type:componentKind`. Applied after layout so emphasis never re-runs it.
  const [emphasis, setEmphasis] = useState<string | null>(null)
  const [layoutNodes, setLayoutNodes] = useState<Node[]>([])
  const [layoutEdges, setLayoutEdges] = useState<Edge[]>([])
  const [laying, setLaying] = useState(false)

  // Emphasis is a presentation pass over the laid-out nodes: no relayout, no
  // lost positions, and clearing it is instant.
  const displayNodes = useMemo(() => {
    if (!emphasis) return layoutNodes
    return layoutNodes.map((n) => {
      const node = (n.data as { node?: { type: string; component_kind?: string } })?.node
      const entry = node ? legendEntryFor(node as never) : undefined
      const key = entry
        ? (entry.componentKind ? `${entry.type}:${entry.componentKind}` : entry.type)
        : null
      return { ...n, data: { ...n.data, dimmed: key !== emphasis } }
    })
  }, [layoutNodes, emphasis])

  // Apply repo filter before layout. Filtering at the canvas level keeps
  // the backend query simple — tenant mesh always returns everything,
  // frontend slices for the user.
  const visibleMesh = useMemo<MeshResponse>(() => {
    // Escalation-path highlight takes precedence over both zoom and repo
    // filter — when a path is active, show exactly its nodes (any hop
    // count, not just 1-hop neighbors).
    if (activeFocusPath && activeFocusPath.length > 0) {
      const neighborhood = new Set<string>(activeFocusPath)
      return {
        ...mesh,
        nodes: mesh.nodes.filter((n) => neighborhood.has(n.id)),
        edges: mesh.edges.filter(
          (e) => neighborhood.has(e.source) && neighborhood.has(e.target),
        ),
        repos: mesh.repos.filter((r) =>
          mesh.nodes.some((n) => neighborhood.has(n.id) && n.repo_id === r.id),
        ),
      }
    }
    // M6 — zoom takes precedence over repo filter. When zoomed, show
    // only the zoomed node + its 1-hop neighbors.
    if (zoomedNodeId) {
      const neighborhood = new Set<string>([zoomedNodeId])
      for (const e of mesh.edges) {
        if (e.source === zoomedNodeId) neighborhood.add(e.target)
        if (e.target === zoomedNodeId) neighborhood.add(e.source)
      }
      return {
        ...mesh,
        nodes: mesh.nodes.filter((n) => neighborhood.has(n.id)),
        edges: mesh.edges.filter(
          (e) => neighborhood.has(e.source) && neighborhood.has(e.target),
        ),
        repos: mesh.repos.filter((r) =>
          mesh.nodes.some((n) => neighborhood.has(n.id) && n.repo_id === r.id),
        ),
      }
    }
    if (repoFilter.length === 0) {
      // M7b — edge-confidence filter applies even without repo filter
      const filteredEdges = edgeFilter === 'all'
        ? mesh.edges
        : mesh.edges.filter((e) => edgeFilter === 'observed' ? e.observed : !e.observed)
      return { ...mesh, edges: filteredEdges }
    }
    const visibleHints = new Set(
      mesh.nodes
        .filter((n) => (n.repo_id !== null && repoFilter.includes(n.repo_id))
                       || n._source === 'synthetic')
        .map((n) => n.id),
    )
    const repoFilteredEdges = mesh.edges.filter(
      (e) => visibleHints.has(e.source) && visibleHints.has(e.target),
    )
    const finalEdges = edgeFilter === 'all'
      ? repoFilteredEdges
      : repoFilteredEdges.filter((e) => edgeFilter === 'observed' ? e.observed : !e.observed)
    return {
      ...mesh,
      nodes: mesh.nodes.filter((n) => visibleHints.has(n.id)),
      edges: finalEdges,
      repos: mesh.repos.filter((r) => repoFilter.includes(r.id)),
    }
  }, [mesh, repoFilter, zoomedNodeId, activeFocusPath, edgeFilter])

  // Recompute layout when mesh, repo filter, or overlay changes.
  // Overlay-only changes don't strictly need a relayout but the
  // recomputed `data.overlay` flows into node renderers, and a layout
  // result that already reflects the current overlay is simpler than
  // a parallel re-render path.
  useEffect(() => {
    let cancelled = false
    setLaying(true)
    // Escalation-path UI fix (2026-07-29) — pairs of consecutive path
    // nodes, used below to mark the edges between them for a distinct
    // highlight treatment (edgeRenderers.tsx reads data.pathHighlighted).
    // Directional (source->target), matching the path's own
    // entry-to-via direction.
    const pathEdgeKeys = activeFocusPath && activeFocusPath.length > 1
      ? new Set(
          activeFocusPath.slice(0, -1).map((h, i) => `${h}->${activeFocusPath[i + 1]}`),
        )
      : null
    layoutMesh(visibleMesh)
      .then(({ nodes, edges }) => {
        if (cancelled) return
        setLayoutNodes(
          nodes.map((n) => ({
            ...n,
            data: { ...n.data, overlay },
          })),
        )
        setLayoutEdges(
          pathEdgeKeys
            ? edges.map((e) => ({
                ...e,
                data: {
                  ...e.data,
                  pathHighlighted: pathEdgeKeys.has(`${e.source}->${e.target}`),
                },
              }))
            : edges,
        )
      })
      .finally(() => {
        if (!cancelled) setLaying(false)
      })
    return () => {
      cancelled = true
    }
  }, [visibleMesh, overlay, activeFocusPath])

  const handleNodeClick: NodeMouseHandler = (evt, node) => {
    if (node.type !== 'mesh') return
    const meshNode = (node.data as { node: MeshNode })?.node
    if (!meshNode) return
    // M6 — Shift-click (or Alt-click) zooms into this node's
    // neighborhood instead of opening the drawer. Plain click keeps the
    // existing drawer-open behavior so the gesture is non-breaking.
    if (evt.shiftKey || evt.altKey) {
      setActiveFocusPath(null)
      setZoomedNodeId(meshNode.id)
      return
    }
    onNodeClick?.(meshNode)
  }

  const handleEdgeClick: EdgeMouseHandler = (_evt, edge) => {
    onEdgeClick?.(edge.id)
  }

  if (loading || laying) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Loading mesh…' : 'Laying out topology…'}
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Failed to load mesh: {error}</Alert>
      </Box>
    )
  }

  if (mesh.nodes.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No agents detected in this scope yet.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Once a scan completes, the topology will populate here.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height, position: 'relative' }}>
      <MeshArrowDefs />

      {/* Overlay toggles + scope summary header */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            <strong>{mesh.meta.agent_count}</strong> agents ·{' '}
            <strong>{mesh.meta.edge_count}</strong> edges
            {mesh.meta.synthetic_count > 0 && (
              <> · <strong>{mesh.meta.synthetic_count}</strong> external</>
            )}
            {mesh.scope === 'tenant' && (
              <> · {mesh.meta.scan_count} scans across {mesh.meta.repo_count} repos</>
            )}
          </Typography>
          {/* UX5 — first-visit orientation popover. Stays in toolbar so
              users can re-open it any time. Auto-opens once via localStorage. */}
          <OrientationHint scope={mesh.scope} />
          {mesh.scope === 'tenant' && mesh.repos.length > 1 && onRepoFilterChange && (
            <RepoFilter
              repos={mesh.repos.map((r) => ({ id: r.id, name: r.name }))}
              value={repoFilter}
              onChange={onRepoFilterChange}
            />
          )}
        </Stack>
        <ToggleButtonGroup
          size="small"
          value={overlay}
          exclusive
          onChange={(_, v) => v && setOverlay(v)}
        >
          {/*
            Overlay legend (anti-duplication notes):
              Risk          — finding severity (security)        → border color
              Blast Radius  — downstream count (engineering)     → node size
              Drift         — what changed last 30d (engineering) → pulsing glow
              Compliance    — control alignment per framework    → 4-color border + drill-down panel (M5b)
              Framework    — tech stack categorization           → categorical border color
            A Platform lens existed here briefly and was removed: hosting is already on
            the agent card and the Deployment card, and on a self-hosted estate it
            rendered a uniformly grey canvas that read as broken rather than as an
            answer. A lens has to earn a slot in a five-item toolbar.
            Each overlay uses a DISTINCT visual axis so users can't confuse two for the same thing.
          */}
          <Tooltip title="Security view — color by finding severity" placement="top">
            <ToggleButton value="risk" sx={{ textTransform: 'none' }}>
              Risk
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Engineering view — node size scales with downstream impact" placement="top">
            <ToggleButton value="blast_radius" sx={{ textTransform: 'none' }}>
              Blast Radius
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Engineering view — pulsing glow on agents changed in the last 30 days" placement="top">
            <ToggleButton value="drift" sx={{ textTransform: 'none' }}>
              Drift
            </ToggleButton>
          </Tooltip>
          <Tooltip title="GRC view — 4-color compliance status per framework" placement="top">
            <ToggleButton
              value="compliance"
              sx={{ textTransform: 'none' }}
              disabled={availableFrameworks.length === 0}
            >
              Compliance
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Categorical view — color by framework" placement="top">
            <ToggleButton value="framework" sx={{ textTransform: 'none' }}>
              Framework
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Stack>

      {/* UX2 + UX3 — Lens legend bar: per-lens headline question + visual
          key + empty-state messaging. Sits directly under the overlay
          toggles so switching modes shows IMMEDIATE textual + visual
          change (the user's #1 complaint about V1 was that lenses
          looked indistinguishable). */}
      <LensLegend
        overlay={overlay}
        mesh={visibleMesh}
        scope={mesh.scope}
        complianceFramework={complianceFramework}
        availableFrameworks={availableFrameworks}
        emphasis={emphasis}
        onEmphasisChange={setEmphasis}
      />

      {/* Framework picker — only visible when Compliance overlay is active.
          Decoupled from the toggle row so it can grow into a richer panel
          (control list, drill-down) without crowding the toggles. */}
      {overlay === 'compliance' && availableFrameworks.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary">
            Framework:
          </Typography>
          <select
            value={complianceFramework || ''}
            onChange={(e) => onComplianceFrameworkChange?.(e.target.value || null)}
            style={{
              fontSize: 12, padding: '4px 8px', border: '1px solid #ccc',
              borderRadius: 4, background: 'white',
            }}
          >
            <option value="">— select framework —</option>
            {availableFrameworks.map((f) => (
              <option key={f.key} value={f.key}>{f.display_name || f.key}</option>
            ))}
          </select>
        </Stack>
      )}

      {/* Escalation-path breadcrumb. Visible only when a path is active —
          takes precedence over the zoom breadcrumb below (both are never
          shown at once, since setting one clears the other). */}
      {activeFocusPath && activeFocusPath.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: '#fdecea' }}
        >
          <Button
            size="small"
            onClick={() => setActiveFocusPath(null)}
            sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
          >
            ← Back to full topology
          </Button>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Escalation path:{' '}
            {activeFocusPath
              .map((hint) => mesh.nodes.find((n) => n.id === hint)?.name || hint)
              .join(' → ')}
          </Typography>
        </Stack>
      )}

      {/* M6 — Zoom breadcrumb. Visible only when zoomed into a node. */}
      {zoomedNode && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: '#fff3e0' }}
        >
          <Button
            size="small"
            onClick={() => setZoomedNodeId(null)}
            sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
          >
            ← Back to full topology
          </Button>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Zoomed: {zoomedNode.name || '(unnamed)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            · {visibleMesh.nodes.length - 1} neighbors · {visibleMesh.edges.length} edges
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Tip: shift-click any node to zoom into its neighborhood
          </Typography>
        </Stack>
      )}

      {/* M7b — Edge-confidence filter. Compact buttons below the toggles. */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Edges:
        </Typography>
        {(['all', 'observed', 'inferred'] as const).map((mode) => (
          <Button
            key={mode}
            size="small"
            variant={edgeFilter === mode ? 'contained' : 'outlined'}
            onClick={() => setEdgeFilter(mode)}
            sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0, fontSize: 11, height: 22 }}
          >
            {mode === 'all' ? 'Show all' : mode === 'observed' ? 'Only observed' : 'Only inferred'}
          </Button>
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          ({visibleMesh.edges.length} visible)
        </Typography>
      </Stack>

      <Box
        sx={{
          // Base canvas height after subtracting the header (50), the
          // legend bar (~32), and the edge-filter row (32). Optional
          // zoom + compliance picker each subtract another ~38px.
          height: `calc(100% - 114px ${zoomedNode ? '- 38px' : ''} ${overlay === 'compliance' && availableFrameworks.length > 0 ? '- 38px' : ''})`,
        }}
      >
        <ReactFlow
          nodes={displayNodes}
          edges={layoutEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          fitView
          minZoom={0.2}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={20} size={1} />
          <Controls position="bottom-right" showInteractive={false} />
          <MiniMap
            pannable
            zoomable
            position="bottom-left"
            nodeStrokeWidth={2}
            nodeColor={(node) => {
              if (node.type !== 'mesh') return '#90a4ae'
              const meshNode = (node.data as { node: MeshNode })?.node
              if (!meshNode || meshNode._source === 'synthetic') return '#cfd8dc'
              if (meshNode.findings_severity_max === 'critical') return '#d32f2f'
              if (meshNode.findings_severity_max === 'high') return '#ed6c02'
              if (meshNode.findings_severity_max === 'medium') return '#f9a825'
              return '#bdbdbd'
            }}
          />
        </ReactFlow>
      </Box>
    </Box>
  )
}

interface RepoFilterProps {
  repos: { id: string; name: string }[]
  /** Selected repositories. Empty means all of them. */
  value: string[]
  onChange: (ids: string[]) => void
}

/**
 * Which repositories the mesh is drawn from.
 *
 * This offered one repository or the whole estate and nothing in between, and
 * "these three services" is the question somebody actually has once an estate
 * has forty. Selecting is now additive: each repository toggles, and "All
 * repos" clears the selection rather than being a choice of its own.
 *
 * It also stopped listing after five, with a caption suggesting the reader
 * drill in from the repo page instead. A truncated list is defensible for a
 * single choice and not for a multiple one — a repository you cannot see is
 * one you cannot add — so all of them are shown and the row wraps.
 */
function RepoFilter({ repos, value, onChange }: RepoFilterProps) {
  if (repos.length <= 1) return null
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Button
        size="small"
        variant={value.length === 0 ? 'contained' : 'outlined'}
        onClick={() => onChange([])}
        sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
      >
        All repos
      </Button>
      {repos.map((r) => (
        <Button
          key={r.id}
          size="small"
          variant={value.includes(r.id) ? 'contained' : 'outlined'}
          onClick={() => toggle(r.id)}
          sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
        >
          {r.name}
        </Button>
      ))}
      {value.length > 1 && (
        <Typography variant="caption" color="text.secondary">
          {value.length} of {repos.length}
        </Typography>
      )}
    </Stack>
  )
}
